import ErrorCard from "components/UI/ErrorCard";
import LoadingDeveloperApplications from "components/UI/loading/LoadingDeveloperApplications";

import AccentTitle from "components/UI/AccentTitle";
import { ApplicationNav } from "../ApplicationNav";
import {
  ApplicationsListProvider,
  useApplicationsListContext,
} from "./ApplicationsListProvider";
import Button from "components/UI/Button";
import { PlusSmIcon } from "@heroicons/react/solid";
import { isObjectEmpty } from "utils";
import { FilterBadgesList } from "components/FilterBadgesList";
import { useParams } from "react-router-dom";
import { formConfigurationMapping } from "features/ApplicationForm/FormConfig";
import { useApplicationForm } from "hooks/ReactQueryHooks/useApplicationFromQuery";
import { FilterIcon } from "@heroicons/react/outline";
import { usePermission } from "hooks/usePermission";
import UnAuthorizedMessage from "components/Permission/UnauthorizedMessage";
import { EPermission } from "types/Permission";

const ApplicationsListTable = () => {
  const { applicationFormId } = useParams<string>();
  const {
    data,
    filterBadges,
    toggleFilterModal,
    removeFilterHandler,
    fetchApplications,
  } = useApplicationsListContext();

  const fetchApplicationForm = useApplicationForm({
    formId: applicationFormId!,
  });
  const applicationForm = fetchApplicationForm?.data?.data;

  const applicationFormConfig = formConfigurationMapping.find(
    (fcm) => fcm.formCode === applicationForm?.templateCode
  );

  if (fetchApplications.isLoading) {
    return <LoadingDeveloperApplications />;
  }

  if (!applicationFormConfig) {
    return (
      <div className="my-5 h-screen/2 flex items-center">
        <ErrorCard
          errorMsg="Oops! This page is currently under construction. Please check back soon."
          errorTitle="Page Under Construction"
        />
      </div>
    );
  }

  const ApplicationListComponent =
    applicationFormConfig.applicationsListComponent;

  return (
    <div className="relative">
      <div className="flex flex-col gap-2 px-4 pt-4 border-t">
        <div className="flex flex-col flex-wrap gap-2 items-start">
          <Button
            label="Filter"
            btnStyle="neutral"
            btnSize="md"
            icon={<FilterIcon className="h-5 w-5" />}
            onClick={toggleFilterModal}
          />
          {!isObjectEmpty(filterBadges) && (
            <FilterBadgesList
              displayFilters={filterBadges}
              handleRemoveFilter={removeFilterHandler}
            />
          )}
        </div>
        {!isObjectEmpty(filterBadges) && (
          <span className="m-0 text-neutralColor-normal mb-2">
            {data?.meta?.count} applications found.
          </span>
        )}
      </div>
      {fetchApplications.isSuccess && data && (
        <div className="m-4">
          <ApplicationListComponent />
        </div>
      )}
      {!fetchApplications.isFetching && fetchApplications.isError && (
        <div className="flex-grow flex justify-center items-center text-neutralColor-normal h-screen/2">
          <ErrorCard
            errorTitle="Fetch Error"
            errorMsg="We are unable to pull the list of applications from the database at this point. Please try again later. "
          />
        </div>
      )}
    </div>
  );
};

export const ApplicationsList = () => {
  const { hasPermission } = usePermission();

  if (!hasPermission([EPermission.LIST_APPLICATIONS])) {
    return (
      <UnAuthorizedMessage
        title="Unauthorized"
        message="You do not have permission to view this application form applications."
      />
    );
  }
  return (
    <>
      <ApplicationNav />
      <div className="flex flex-col w-full bg-white">
        <div className="border-neutralColor-light flex justify-between rounded-t-lg">
          <AccentTitle title="Applications" />
        </div>
        <ApplicationsListProvider>
          <ApplicationsListTable />
        </ApplicationsListProvider>
      </div>
    </>
  );
};
